/**
 * Market Intelligence API Service
 * Service for fetching market intelligence and IEA policy data
 */

import { API_BASE_URL as MARKET_INTELLIGENCE_API_BASE } from './apiConfig';

/**
 * Get market intelligence data
 * @returns {Promise<Object>} Market intelligence data
 */
export const getMarketIntelligenceData = async () => {
  try {
    const response = await fetch(`${MARKET_INTELLIGENCE_API_BASE}/api/market-intelligence`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching market intelligence data:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Get policy market intelligence
 * @returns {Promise<Object>} Policy market intelligence data
 */
export const getPolicyMarketIntelligence = async () => {
  try {
    const response = await fetch(`${MARKET_INTELLIGENCE_API_BASE}/api/policy-market-intelligence`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching policy market intelligence:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Get policy volatility data
 * @param {string} country - Optional country filter
 * @param {number} days - Number of days to look back
 * @returns {Promise<Object>} Policy volatility data
 */
export const getPolicyVolatility = async (country = null, days = 30) => {
  try {
    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('days', days.toString());
    
    const response = await fetch(`${MARKET_INTELLIGENCE_API_BASE}/api/policy-volatility?${params}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching policy volatility:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Get policy alerts
 * @param {string} severity - Optional severity filter
 * @param {number} limit - Maximum number of alerts
 * @returns {Promise<Object>} Policy alerts data
 */
export const getPolicyAlerts = async (severity = null, limit = 10) => {
  try {
    const params = new URLSearchParams();
    if (severity) params.append('severity', severity);
    params.append('limit', limit.toString());
    
    const response = await fetch(`${MARKET_INTELLIGENCE_API_BASE}/api/policy-alerts?${params}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching policy alerts:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Get price trends data
 * @param {string} minerals - Comma-separated list of minerals
 * @returns {Promise<Object>} Price trends data
 */
export const getPriceTrends = async (minerals = null) => {
  try {
    const params = new URLSearchParams();
    if (minerals) params.append('minerals', minerals);
    
    const response = await fetch(`${MARKET_INTELLIGENCE_API_BASE}/api/price-trends?${params}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching price trends:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Get IEA policy summary for dashboard
 * @returns {Promise<Object>} IEA policy summary data
 */
export const getIeaPolicySummary = async () => {
  try {
    const [marketData, policyData, volatilityData, alertsData] = await Promise.all([
      getMarketIntelligenceData(),
      getPolicyMarketIntelligence(),
      getPolicyVolatility(),
      getPolicyAlerts()
    ]);
    
    // Extract relevant data for IEA policy section
    const summary = {
      totalPolicies: 0,
      activePolicies: 0,
      countries: 0,
      recentUpdates: 0,
      policyTrends: [],
      volatilityMetrics: {},
      recentAlerts: []
    };
    
    if (marketData.success && marketData.data) {
      const data = marketData.data;
      
      // Extract policy information from market intelligence data
      if (data.tradePolicyEvents) {
        summary.totalPolicies = data.tradePolicyEvents.length;
        summary.activePolicies = data.tradePolicyEvents.filter(event => 
          event.status === 'Active' || event.status === 'active'
        ).length;
        
        // Get unique countries
        const countries = new Set();
        data.tradePolicyEvents.forEach(event => {
          if (event.countries) {
            event.countries.forEach(country => countries.add(country));
          }
        });
        summary.countries = countries.size;
        
        // Get recent updates (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        summary.recentUpdates = data.tradePolicyEvents.filter(event => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= thirtyDaysAgo;
        }).length;
        
        // Get policy trends
        summary.policyTrends = data.tradePolicyEvents.slice(0, 5);
      }
    }
    
    if (policyData.success && policyData.data) {
      // Extract additional policy information
      const policyInfo = policyData.data;
      if (policyInfo.policies) {
        summary.totalPolicies += policyInfo.policies.length;
      }
    }
    
    if (volatilityData.success && volatilityData.data) {
      summary.volatilityMetrics = volatilityData.data;
    }
    
    if (alertsData.success && alertsData.data) {
      summary.recentAlerts = alertsData.data.slice(0, 5);
    }
    
    return summary;
  } catch (error) {
    console.error('Error fetching IEA policy summary:', error);
    return {
      totalPolicies: 0,
      activePolicies: 0,
      countries: 0,
      recentUpdates: 0,
      policyTrends: [],
      volatilityMetrics: {},
      recentAlerts: []
    };
  }
};

/**
 * Format policy date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatPolicyDate = (dateString) => {
  if (!dateString) return 'Recent';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return 'Recent';
  }
};

/**
 * Get policy type color for UI display
 * @param {string} type - Policy type
 * @returns {string} CSS color class
 */
export const getPolicyTypeColor = (type) => {
  const colorMap = {
    'export ban': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    'tariff change': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    'trade agreement': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    'regulatory change': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    'policy update': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
  };
  return colorMap[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
};

const marketIntelligenceApi = {
  getMarketIntelligenceData,
  getPolicyMarketIntelligence,
  getPolicyVolatility,
  getPolicyAlerts,
  getPriceTrends,
  getIeaPolicySummary,
  formatPolicyDate,
  getPolicyTypeColor,
};

export default marketIntelligenceApi;
